<template>
  <div>
    <!-- Summary dashboard -->
    <v-row class="mb-1" dense>
      <v-col cols="6" md="3">
        <v-card
          outlined
          class="rounded-lg po-summary-tile"
          :class="{ 'po-summary-active': !staleOnly && filterStatus === 'All' }"
          @click="applyStatusTile('All')"
        >
          <v-card-text class="py-3">
            <div class="text-caption grey--text text-uppercase">Total POs</div>
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-file-document-multiple</v-icon>
              <span class="text-h5 font-weight-bold">{{ poSummary.total }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card
          outlined
          class="rounded-lg po-summary-tile"
          :class="{ 'po-summary-active': !staleOnly && filterStatus === 'Pending' }"
          @click="applyStatusTile('Pending')"
        >
          <v-card-text class="py-3">
            <div class="text-caption grey--text text-uppercase">Pending</div>
            <div class="d-flex align-center">
              <v-icon color="orange" class="mr-2">mdi-progress-clock</v-icon>
              <span class="text-h5 font-weight-bold">{{ poSummary.pending }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card
          outlined
          class="rounded-lg po-summary-tile"
          :class="{ 'po-summary-active': !staleOnly && filterStatus === 'Completed' }"
          @click="applyStatusTile('Completed')"
        >
          <v-card-text class="py-3">
            <div class="text-caption grey--text text-uppercase">Completed</div>
            <div class="d-flex align-center">
              <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
              <span class="text-h5 font-weight-bold">{{ poSummary.completed }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card
          outlined
          class="rounded-lg po-summary-tile"
          :class="{
            'po-summary-active': staleOnly,
            'po-summary-alert': poSummary.stale > 0,
          }"
          @click="toggleStaleOnly"
        >
          <v-card-text class="py-3">
            <div class="d-flex align-center justify-space-between">
              <div class="text-caption grey--text text-uppercase">
                No update ≥ {{ staleThreshold }}d
              </div>
              <v-select
                v-model="staleThreshold"
                :items="staleThresholdOptions"
                dense
                hide-details
                class="po-stale-days pa-0 ma-0"
                style="max-width: 64px"
                @click.stop
              ></v-select>
            </div>
            <div class="d-flex align-center">
              <v-icon :color="poSummary.stale > 0 ? 'red' : 'grey'" class="mr-2">
                mdi-clock-alert
              </v-icon>
              <span
                class="text-h5 font-weight-bold"
                :class="{ 'red--text': poSummary.stale > 0 }"
                >{{ poSummary.stale }}</span
              >
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="staleOnly" class="mb-1" dense>
      <v-col cols="12">
        <v-alert type="warning" dense text class="mb-0 d-flex align-center">
          Showing {{ filteredPurchaseOrders.length }} open PO(s) with no status
          update for {{ staleThreshold }}+ days.
          <v-btn x-small text class="ml-2" @click="toggleStaleOnly">Clear</v-btn>
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="align-center">
      <v-col cols="12" md="3">
        <v-select
          v-model="searchField"
          :items="searchFieldOptions"
          label="Search By"
          prepend-icon="mdi-filter-variant"
          outlined
          dense
        ></v-select>
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          v-model="search"
          :label="`Search ${searchField}`"
          prepend-icon="mdi-magnify"
          clearable
          outlined
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="filterStatus"
          :items="['All', 'Completed', 'Pending']"
          label="Filter by Status"
          prepend-icon="mdi-filter"
          outlined
          dense
          clearable
        ></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-select
          v-model="filterPurchaseType"
          :items="['All', 'Canvass', 'Direct']"
          label="Filter by Type"
          prepend-icon="mdi-filter-variant"
          outlined
          dense
          clearable
        ></v-select>
      </v-col>
    </v-row>
    <v-row class="mb-2">
      <v-col cols="12">
        <v-btn color="success" @click="$refs.directPurchaseDialog.open()">
          <v-icon left>mdi-cart-plus</v-icon>
          Direct Purchase
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="selectedPOs.length > 0" class="mb-2">
      <v-col cols="12">
        <v-alert type="info" dense outlined class="mb-0">
          <v-row align="center" no-gutters>
            <v-col>
              <strong>{{ selectedPOs.length }} PO(s) selected</strong>
              <span v-if="canCombineSelected" class="ml-2">
                - Same supplier detected
              </span>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="primary"
                small
                @click="openCombineDialog"
                :disabled="!canCombineSelected || selectedPOs.length < 2"
              >
                <v-icon left small>mdi-merge</v-icon>
                Combine POs
              </v-btn>
              <v-btn
                color="grey"
                text
                small
                class="ml-2"
                @click="clearSelection"
              >
                Clear Selection
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-data-table
        v-model="selectedPOs"
        :item-key="'id'"
        :headers="headers"
        :items="filteredPurchaseOrders"
        :search="search"
        :custom-filter="customSearchFilter"
        :loading="loading"
        show-select
        class="elevation-1"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, -1],
        }"
      >
        <!-- PO ID -->
        <template v-slot:[`item.id`]="{ item }">
          <div class="d-flex align-center">
            <v-chip color="primary" outlined>
              PO-{{ String(item.id).padStart(4, "0") }}
            </v-chip>
            <v-chip
              v-if="item.is_combined"
              color="purple"
              x-small
              class="ml-1"
              title="This PO was created by combining multiple POs"
            >
              <v-icon x-small left>mdi-merge</v-icon>
              Combined
            </v-chip>
            <v-chip
              v-if="item.is_modified"
              color="orange"
              x-small
              class="ml-1"
              title="This PO has been modified"
            >
              <v-icon x-small left>mdi-pencil</v-icon>
              Modified
            </v-chip>
          </div>
        </template>
        <!-- IS VAT -->
        <template v-slot:[`item.is_vat_inclusive`]="{ item }">
          <v-chip :color="item.is_vat_inclusive ? 'green' : 'red'" dark small>
            {{ item.is_vat_inclusive ? "VAT Inclusive" : "VAT Exclusive" }}
          </v-chip>
        </template>

        <!-- Purchase Type -->
        <template v-slot:[`item.purchase_type`]="{ item }">
          <v-chip
            :color="item.purchase_type === 'direct' ? 'teal' : 'blue'"
            dark
            x-small
          >
            {{ item.purchase_type === "direct" ? "Direct" : "Canvass" }}
          </v-chip>
        </template>

        <!-- Customer PO -->
        <template v-slot:[`item.canvass`]="{ item }">
          <v-chip
            v-if="item.canvass && item.canvass.customer_purchase_order"
            color="blue"
            outlined
          >
            {{ item.canvass.customer_purchase_order.po_number }}
          </v-chip>
          <v-chip
            v-else-if="item.customer_purchase_order"
            color="blue"
            outlined
          >
            {{ item.customer_purchase_order.po_number }}
          </v-chip>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- Customer Name -->
        <template v-slot:[`item.customer_name`]="{ item }">
          <span
            class="font-weight-medium"
            v-if="
              item.canvass &&
              item.canvass.customer_purchase_order &&
              item.canvass.customer_purchase_order.customer
            "
          >
            {{ item.canvass.customer_purchase_order.customer.customer_name }}
          </span>
          <span
            class="font-weight-medium"
            v-else-if="
              item.customer_purchase_order &&
              item.customer_purchase_order.customer
            "
          >
            {{ item.customer_purchase_order.customer.customer_name }}
          </span>
          <v-chip
            v-else-if="item.purchase_type === 'direct'"
            color="teal"
            outlined
            small
          >
            <v-icon x-small left>mdi-cart-plus</v-icon>
            Direct Purchase
          </v-chip>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- Supplier -->
        <template v-slot:[`item.supplier`]="{ item }">
          <div v-if="item.supplier">
            <strong>{{ item.supplier.supplier_name }}</strong>
          </div>
          <div v-else-if="item.remarks">
            <span class="orange--text">{{
              extractSupplierFromRemarks(item.remarks)
            }}</span>
          </div>
          <span v-else class="grey--text">Not Set</span>
        </template>

        <!-- Remarks -->
        <template v-slot:[`item.remarks`]="{ item }">
          <span v-if="item.remarks">
            {{ item.remarks }}
          </span>
          <span
            v-else-if="
              item.canvass &&
              item.canvass.purchase_requisition &&
              item.canvass.purchase_requisition.remarks
            "
          >
            {{ item.canvass.purchase_requisition.remarks }}
          </span>
          <span v-else class="grey--text">N/A</span>
        </template>

        <!-- Items Count -->
        <template v-slot:[`item.items_count`]="{ item }">
          <v-chip color="primary" outlined>
            {{ item.items && item.items.length ? item.items.length : 0 }} items
          </v-chip>
        </template>

        <!-- Status -->
        <template v-slot:[`item.is_completed`]="{ item }">
          <v-select
            :value="item.status || 'pending'"
            :items="statusOptions"
            dense
            hide-details
            outlined
            @change="updateStatus(item, $event)"
            :loading="item.updatingStatus"
            :disabled="item.updatingStatus"
            :color="getStatusColor(item.status)"
          >
            <template v-slot:selection="{ item: statusItem }">
              <v-chip :color="getStatusColor(statusItem.value)" dark small>
                {{ statusItem.text }}
              </v-chip>
            </template>
          </v-select>
        </template>

        <!-- Created By -->
        <template v-slot:[`item.created_by`]="{ item }">
          <span class="font-weight-medium">{{
            item.created_by && item.created_by.name
              ? item.created_by.name
              : "N/A"
          }}</span>
        </template>

        <!-- Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center">
            <v-btn icon color="primary" @click="viewItem(item)" title="View">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon color="info" @click="printItem(item)" title="Print">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
            <v-btn
              icon
              color="error"
              @click="deleteItem(item)"
              title="Delete"
              :loading="item.deleting"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- No Data -->
        <template v-slot:no-data>
          <div class="text-center py-6">
            <v-icon size="64" color="grey lighten-1">mdi-cart-outline</v-icon>
            <p class="grey--text mt-2">No purchase orders found</p>
          </div>
        </template>
      </v-data-table>
    </v-row>

    <!-- View Dialog -->
    <v-dialog v-model="viewDialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <span class="text-h5">Purchase Order Details</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeViewDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" v-if="selectedItem">
          <v-row dense>
            <!-- Left Column -->
            <v-col cols="12" md="6">
              <div class="info-row">
                <strong>PO Number:</strong> PO-{{
                  String(selectedItem.id).padStart(4, "0")
                }}
              </div>
              <div class="info-row">
                <strong>Supplier:</strong>
                <span
                  v-if="selectedItem.supplier"
                  class="font-weight-bold text-h6"
                >
                  {{ selectedItem.supplier.supplier_name }}
                </span>
                <span v-else-if="selectedItem.remarks" class="orange--text">
                  {{ extractSupplierFromRemarks(selectedItem.remarks) }}
                </span>
                <span v-else class="grey--text">Not Set</span>
              </div>
              <div class="info-row d-flex align-center">
                <strong>Purchase Type:</strong>
                <v-chip
                  :color="
                    selectedItem.purchase_type === 'direct' ? 'teal' : 'blue'
                  "
                  dark
                  small
                  class="ml-1"
                >
                  {{
                    selectedItem.purchase_type === "direct"
                      ? "Direct Purchase"
                      : "Via Canvass"
                  }}
                </v-chip>
                <v-btn
                  v-if="can('supplier_po.edit_details')"
                  icon
                  small
                  color="primary"
                  @click="openEditPoDetailsDialog"
                  title="Edit PO Details"
                  class="ml-1"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="info-row d-flex align-center">
                <strong>Government PO:</strong>
                <v-chip
                  v-if="
                    selectedItem.canvass &&
                    selectedItem.canvass.customer_purchase_order
                  "
                  color="blue"
                  outlined
                  small
                >
                  {{ selectedItem.canvass.customer_purchase_order.po_number }}
                </v-chip>
                <v-chip
                  v-else-if="selectedItem.customer_purchase_order"
                  color="blue"
                  outlined
                  small
                >
                  {{ selectedItem.customer_purchase_order.po_number }}
                </v-chip>
                <span v-else class="grey--text">N/A</span>
              </div>
              <div class="info-row d-flex align-center">
                <strong>Purchase Request:</strong>
                <v-chip
                  v-if="selectedItem.internal_purchase_order"
                  color="teal"
                  outlined
                  small
                >
                  PR-{{
                    String(selectedItem.internal_purchase_order.id).padStart(
                      4,
                      "0"
                    )
                  }}
                </v-chip>
                <span v-else class="grey--text">N/A</span>
              </div>
              <div class="info-row">
                <strong>Remarks:</strong>
                {{ selectedItem.remarks || "N/A" }}
              </div>
            </v-col>
            <!-- Right Column -->
            <v-col cols="12" md="6">
              <div class="info-row">
                <strong>Status:</strong>
                <v-chip
                  class="ml-2"
                  :color="selectedItem.is_completed ? 'green' : 'orange'"
                  dark
                  small
                >
                  {{ selectedItem.is_completed ? "Completed" : "Pending" }}
                </v-chip>
              </div>
              <div class="info-row">
                <strong>VAT Status:</strong>
                <v-chip
                  class="ml-2"
                  :color="selectedItem.is_vat_inclusive ? 'green' : 'red'"
                  dark
                  small
                  >{{
                    selectedItem.is_vat_inclusive
                      ? "VAT Inclusive"
                      : "VAT Exclusive"
                  }}</v-chip
                >
                <v-btn
                  v-if="can('supplier_po.edit_vat')"
                  icon
                  small
                  color="primary"
                  @click="openEditVatDialog"
                  title="Edit VAT Status"
                  class="ml-2"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="info-row d-flex align-center">
                <strong>Batch Import:</strong>
                <v-chip
                  v-if="selectedItem.import_batch"
                  class="ml-2"
                  color="deep-purple"
                  outlined
                  small
                >
                  <v-icon left small>mdi-package-variant-closed</v-icon>
                  {{ selectedItem.import_batch.batch_description }}
                </v-chip>
                <span v-else class="grey--text ml-2">Not Generated</span>
                <v-btn
                  v-if="can('supplier_po.edit_batch_import')"
                  icon
                  small
                  color="primary"
                  @click="openEditBatchImportDialog"
                  title="Edit Batch Import Code"
                  class="ml-2"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <div class="info-row">
                <strong>Created By:</strong>
                {{
                  selectedItem.created_by ? selectedItem.created_by.name : "N/A"
                }}
              </div>
              <div class="info-row">
                <strong>Created:</strong>
                {{ formatDate(selectedItem.created_at) }}
              </div>
            </v-col>
          </v-row>

          <!-- Status History (collapsible) -->
          <v-row dense>
            <v-col cols="12">
              <!-- Status History -->
              <div
                v-if="
                  selectedItem.status_history &&
                  selectedItem.status_history.length > 0
                "
              >
                <h3 class="mb-3">Status History</h3>
                <v-timeline dense>
                  <v-timeline-item
                    v-for="(history, index) in sortedStatusHistory(
                      selectedItem.status_history
                    )"
                    :key="index"
                    :color="getStatusColor(history.status)"
                    small
                  >
                    <v-card>
                      <v-card-text class="py-2">
                        <div class="d-flex align-center mb-1">
                          <v-chip
                            :color="getStatusColor(history.status)"
                            dark
                            x-small
                          >
                            <v-icon
                              v-if="history.status === 'item_registered'"
                              x-small
                              left
                            >
                              mdi-package-variant-plus
                            </v-icon>
                            <v-icon
                              v-if="history.status === 'item_added'"
                              x-small
                              left
                            >
                              mdi-plus-circle
                            </v-icon>
                            <v-icon
                              v-if="history.status === 'item_removed'"
                              x-small
                              left
                            >
                              mdi-minus-circle
                            </v-icon>
                            <v-icon
                              v-if="history.status === 'vat_status_changed'"
                              x-small
                              left
                            >
                              mdi-receipt-text-edit
                            </v-icon>
                            {{ capitalizeStatus(history.status) }}
                          </v-chip>
                          <span class="text-caption grey--text ml-2">
                            {{ formatDateTime(history.changed_at) }}
                          </span>
                        </div>
                        <div class="text-caption grey--text">
                          {{
                            history.status === "item_registered"
                              ? "Registered"
                              : history.status === "item_added"
                              ? "Added"
                              : history.status === "item_removed"
                              ? "Removed"
                              : history.status === "vat_status_changed"
                              ? "Changed"
                              : "Changed"
                          }}
                          by: {{ history.changed_by || "Unknown" }}
                        </div>
                        <div
                          v-if="
                            (history.status === 'item_registered' ||
                              history.status === 'item_added' ||
                              history.status === 'item_removed') &&
                            history.item_name
                          "
                          class="text-caption mt-1"
                        >
                          <strong>Item:</strong> {{ history.item_name }}
                          <span v-if="history.item_id" class="ml-1"
                            >(ID: {{ history.item_id }})</span
                          >
                        </div>
                        <div
                          v-if="
                            history.status === 'vat_status_changed' &&
                            history.old_vat_status &&
                            history.new_vat_status
                          "
                          class="text-caption mt-1"
                        >
                          <strong>Changed from:</strong>
                          {{ history.old_vat_status }}
                          <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                          <strong>{{ history.new_vat_status }}</strong>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="d-flex align-center mb-3">
            <h3 class="mb-0">Purchase Order Items</h3>
            <v-spacer></v-spacer>
            <v-btn color="success" small @click="openAddNewItemDialog">
              <v-icon small left>mdi-plus</v-icon>
              Add New Item
            </v-btn>
          </div>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Type</th>
                  <th>Item Name (Brand/Model)</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Unit Cost</th>
                  <th class="text-right">Total</th>
                  <th class="text-center">Inserted</th>
                  <th class="text-center" style="width: 250px">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in selectedItem.items" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <span class="grey--text">{{
                      item.canvass_item
                        ? item.canvass_item.item_type || "-"
                        : "-"
                    }}</span>
                  </td>
                  <td>
                    <strong>{{
                      item.canvass_item
                        ? getWinningBrandModel(item.canvass_item) ||
                          item.canvass_item.item_name ||
                          "N/A"
                        : item.item_name || "N/A"
                    }}</strong>
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? item.canvass_item.qty
                        : item.qty || "-"
                    }}
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? item.canvass_item.unit
                        : item.unit || "-"
                    }}
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? getWinningCost(item.canvass_item)
                        : formatCurrency(item.unit_cost)
                    }}
                  </td>
                  <td class="text-right">
                    {{
                      item.canvass_item
                        ? calculateItemTotal(item.canvass_item)
                        : formatCurrency(
                            (item.qty || 0) * (item.unit_cost || 0)
                          )
                    }}
                  </td>
                  <td class="text-center">
                    <v-chip
                      :color="item.is_inserted_to_items ? 'green' : 'grey'"
                      dark
                      x-small
                    >
                      {{ item.is_inserted_to_items ? "Yes" : "No" }}
                    </v-chip>
                  </td>
                  <td class="text-center">
                    <v-btn
                      v-if="item.canvass_item || item.item_name"
                      x-small
                      :color="item.is_inserted_to_items ? 'grey' : 'success'"
                      @click="
                        item.canvass_item
                          ? openAddItemModal(item.canvass_item, item)
                          : openAddItemModalDirect(item)
                      "
                      :title="
                        item.is_inserted_to_items
                          ? 'Already Added'
                          : 'Create Item'
                      "
                      class="mr-1"
                    >
                      <v-icon x-small left>mdi-package-variant-plus</v-icon>
                      Reg. Item
                    </v-btn>
                    <v-btn
                      x-small
                      color="info"
                      @click="openEditItemDialog(item, index)"
                      title="Edit Item Details"
                      class="mr-1"
                    >
                      <v-icon x-small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      color="error"
                      @click="removeItemFromPO(item, index)"
                      title="Remove Item"
                    >
                      <v-icon x-small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="7" class="text-right">
                    <strong>Grand Total:</strong>
                  </td>
                  <td class="text-right">
                    <strong>{{ calculateGrandTotal(selectedItem) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="printItem(selectedItem)">
            <v-icon left>mdi-printer</v-icon>
            Print / Copy
          </v-btn>
          <v-btn text @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit VAT Status Dialog -->
    <v-dialog v-model="editVatDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-pencil</v-icon>
          <span class="text-h6">Edit VAT Status</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeEditVatDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="text-caption">
              <strong>Current PO:</strong>
              <span v-if="selectedItem">
                PO-{{ String(selectedItem.id).padStart(4, "0") }}
              </span>
            </div>
            <div class="text-caption">
              <strong>Current Status:</strong>
              <span v-if="selectedItem">
                {{
                  selectedItem.is_vat_inclusive
                    ? "VAT Inclusive"
                    : "VAT Exclusive"
                }}
              </span>
            </div>
          </v-alert>

          <v-radio-group v-model="editVatStatus" label="Select VAT Status">
            <v-radio label="VAT Inclusive" :value="true" color="green">
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
                  <span
                    ><strong>VAT Inclusive</strong> - Price includes 12%
                    VAT</span
                  >
                </div>
              </template>
            </v-radio>
            <v-radio label="VAT Exclusive" :value="false" color="red">
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon color="red" class="mr-2">mdi-close-circle</v-icon>
                  <span
                    ><strong>VAT Exclusive</strong> - Price without VAT</span
                  >
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-alert type="warning" outlined dense class="mt-4">
            <v-icon small class="mr-2">mdi-alert</v-icon>
            <span class="text-caption"
              >Changing VAT status will update the purchase order
              immediately.</span
            >
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditVatDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmEditVat"
            :loading="updatingVat"
            :disabled="
              selectedItem && editVatStatus === selectedItem.is_vat_inclusive
            "
          >
            <v-icon left small>mdi-content-save</v-icon>
            Update VAT Status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Batch Import Code Dialog (JMB-234) -->
    <v-dialog v-model="editBatchImportDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-package-variant-closed</v-icon>
          <span class="text-h6">Edit Batch Import Code</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeEditBatchImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="text-caption">
              <strong>Current PO:</strong>
              <span v-if="selectedItem">
                PO-{{ String(selectedItem.id).padStart(4, "0") }}
              </span>
            </div>
            <div class="text-caption">
              <strong>Current Batch:</strong>
              <span v-if="selectedItem">
                {{
                  selectedItem.import_batch
                    ? selectedItem.import_batch.batch_description
                    : "Not Generated"
                }}
              </span>
            </div>
          </v-alert>

          <v-text-field
            v-model="editBatchCode"
            label="Batch Import Code *"
            outlined
            dense
            :loading="batchTagLoading"
            hint="Format: PREFIX-SEQUENCE-BRANCHLETTER (e.g. UM-23-C)"
            persistent-hint
            append-outer-icon="mdi-refresh"
            @click:append-outer="regenerateBatchCode"
          ></v-text-field>

          <v-alert type="warning" outlined dense class="mt-4">
            <v-icon small class="mr-2">mdi-alert</v-icon>
            <span class="text-caption">
              Renames the linked batch import (or creates one if the PO has
              none). Use the refresh icon to auto-generate the supplier's next
              batch tag.
            </span>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditBatchImportDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmEditBatchImport"
            :loading="updatingBatchImport"
            :disabled="!editBatchCode || !editBatchCode.trim()"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Batch Code
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit PO Details Dialog -->
    <v-dialog v-model="editPoDetailsDialog" max-width="550px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-pencil</v-icon>
          <span class="text-h6">Edit PO Details</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeEditPoDetailsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="text-caption">
              <strong>PO Number:</strong>
              <span v-if="selectedItem">
                PO-{{ String(selectedItem.id).padStart(4, "0") }}
              </span>
            </div>
          </v-alert>

          <v-select
            v-model="editPoDetailsForm.purchase_type"
            :items="[
              { text: 'Direct Purchase', value: 'direct' },
              { text: 'Via Canvass', value: 'canvass' },
            ]"
            label="Purchase Type"
            outlined
            dense
          ></v-select>

          <v-autocomplete
            v-model="editPoDetailsForm.customer_purchase_order_id"
            :items="customer_purchase_orders"
            item-text="po_number"
            item-value="id"
            label="Government PO"
            outlined
            dense
            clearable
            :filter="filterCustomerPO"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.po_number }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.customer">
                  {{ item.customer.customer_name }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <v-textarea
            v-model="editPoDetailsForm.remarks"
            label="Remarks"
            outlined
            dense
            rows="3"
          ></v-textarea>

          <v-autocomplete
            v-model="editPoDetailsForm.created_by_id"
            :items="all_users"
            item-text="name"
            item-value="id"
            label="Created By"
            outlined
            dense
            clearable
            :return-object="false"
          ></v-autocomplete>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditPoDetailsDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmEditPoDetails"
            :loading="updatingPoDetails"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Item Modal -->
    <v-dialog v-model="addItemDialog" persistent max-width="900px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-package-variant-plus</v-icon>
          <span class="text-h5">Add Item from Purchase Order</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeAddItemDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-subtitle class="pt-3 pb-0">
          <v-chip color="blue" outlined small>
            <v-icon left small>mdi-information</v-icon>
            Item details pre-filled from purchase order
          </v-chip>
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text class="pt-4" style="max-height: 600px">
          <!-- Similar Items Table -->
          <v-data-table
            v-if="itemForm.item_name != ''"
            :search="itemForm.item_name"
            :items="branch_filtered_items"
            :headers="itemHeaders"
            :items-per-page="3"
            class="elevation-1 mb-4"
          >
            <template v-slot:top>
              <v-toolbar flat color="grey lighten-4">
                <v-toolbar-title class="text-subtitle-2"
                  >Similar Items Found</v-toolbar-title
                >
              </v-toolbar>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                small
                color="yellow darken-2"
                @click="setItem(item)"
                class="mr-1"
              >
                <v-icon small left>mdi-download</v-icon>Use
              </v-btn>
              <v-btn small color="green" dark @click="openRestockFromPO(item)">
                <v-icon small left>mdi-plus-box</v-icon>Add Stock
              </v-btn>
            </template>
          </v-data-table>

          <v-form ref="itemForm">
            <!-- Main Form - Two Column Layout -->
            <v-row>
              <!-- Left Column -->
              <v-col cols="12" md="6">
                <v-subheader class="px-0 font-weight-bold"
                  >Basic Information</v-subheader
                >

                <v-text-field
                  label="Item Name *"
                  v-model="itemForm.item_name"
                  outlined
                  dense
                  prepend-inner-icon="mdi-tag"
                ></v-text-field>

                <v-autocomplete
                  label="Item Type *"
                  v-model="itemForm.item_type_id"
                  :items="item_types"
                  item-text="item_type"
                  item-value="id"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shape"
                ></v-autocomplete>

                <v-autocomplete
                  label="Sub-Type"
                  v-model="itemForm.item_sub_type_id"
                  :items="filteredSubTypes"
                  item-text="item_sub_type"
                  item-value="id"
                  outlined
                  dense
                  clearable
                  :disabled="!itemForm.item_type_id"
                  prepend-inner-icon="mdi-shape-outline"
                  no-data-text="No sub-types for this type"
                ></v-autocomplete>

                <v-select
                  label="Account Title *"
                  :items="chart_of_accounts"
                  item-text="account_title"
                  item-value="id"
                  v-model="itemForm.chart_of_account_id"
                  outlined
                  dense
                  prepend-inner-icon="mdi-book-open-variant"
                ></v-select>

                <v-text-field
                  label="Unit *"
                  v-model="itemForm.unit"
                  outlined
                  dense
                  readonly
                  prepend-inner-icon="mdi-package"
                ></v-text-field>

                <v-autocomplete
                  label="Supplier"
                  v-model="itemForm.supplier"
                  :items="suppliers"
                  item-text="supplier_name"
                  item-value="supplier_name"
                  outlined
                  dense
                  prepend-inner-icon="mdi-truck"
                ></v-autocomplete>
              </v-col>

              <!-- Right Column -->
              <v-col cols="12" md="6">
                <v-subheader class="px-0 font-weight-bold"
                  >Pricing & Stock</v-subheader
                >

                <v-text-field
                  type="number"
                  label="Unit Price *"
                  v-model="itemForm.unit_price"
                  outlined
                  dense
                  readonly
                  prepend-inner-icon="mdi-cash"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Profitable Margin (%)"
                  v-model="itemForm.profitable_margin"
                  @input="calculateSellingPrice"
                  outlined
                  dense
                  prepend-inner-icon="mdi-percent"
                  hint="Profit percentage to add to unit price"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Shipping Cost"
                  v-model="itemForm.shipping_cost"
                  @input="calculateSellingPrice"
                  outlined
                  dense
                  prepend-inner-icon="mdi-truck-delivery"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Selling Price *"
                  v-model="itemForm.selling_price"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cash-multiple"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Initial Import (Quantity) *"
                  v-model="itemForm.initial_import"
                  outlined
                  dense
                  prepend-inner-icon="mdi-package-variant-closed-plus"
                ></v-text-field>

                <v-checkbox
                  v-model="itemForm.is_serialized"
                  label="Serialized item (each unit has a serial number)"
                  dense
                  class="mt-0"
                ></v-checkbox>

                <serial-numbers-input
                  v-if="itemForm.is_serialized"
                  v-model="itemForm.serials_text"
                  :quantity="itemForm.initial_import"
                />

                <v-radio-group v-model="itemForm.is_vat" row>
                  <template v-slot:label>
                    <div class="text-body-2 font-weight-bold">Tax Option</div>
                  </template>
                  <v-radio label="Non-VAT" :value="0"></v-radio>
                  <v-radio label="VAT" :value="1"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <!-- Warranty & Batch Section -->
            <v-divider class="my-4"></v-divider>
            <v-subheader class="px-0 font-weight-bold"
              >Warranty & Batch</v-subheader
            >

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Warranty Count"
                  type="number"
                  v-model="itemForm.warranty_count"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shield-check"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="itemForm.warranty_duration"
                  label="Warranty Duration"
                  :items="['Day/s', 'Week/s', 'Months/s', 'Year']"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-subheader class="px-0 font-weight-bold"
              >Supplier Warranty (Stock Batch)</v-subheader
            >
            <div class="d-flex align-center">
              <v-text-field
                type="number"
                label="Supplier Warranty"
                v-model="itemForm.supplier_warranty_months"
                outlined
                dense
                prepend-inner-icon="mdi-shield-lock"
                class="flex-grow-1 mr-2"
              ></v-text-field>
              <v-select
                v-model="itemForm.supplier_warranty_unit"
                :items="warrantyUnits"
                outlined
                dense
                style="max-width: 140px"
              ></v-select>
            </div>
            <div class="d-flex mb-4">
              <v-chip
                v-for="preset in warrantyPresets[
                  itemForm.supplier_warranty_unit || 'months'
                ]"
                :key="'iw' + preset"
                small
                class="mr-2"
                :color="
                  itemForm.supplier_warranty_months == preset
                    ? 'primary'
                    : 'grey lighten-2'
                "
                :dark="itemForm.supplier_warranty_months == preset"
                @click="itemForm.supplier_warranty_months = preset"
              >
                {{ preset
                }}{{
                  warrantyUnitShort[itemForm.supplier_warranty_unit || "months"]
                }}
              </v-chip>
            </div>

            <div class="d-flex align-center">
              <v-select
                v-model="itemForm.import_batch_id"
                label="Batch Import Description *"
                :items="import_batches"
                item-value="id"
                item-text="batch_description"
                outlined
                dense
                prepend-inner-icon="mdi-package-variant"
                class="flex-grow-1"
              ></v-select>
              <add-batch-import-modal class="ml-2" />
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>

          <v-btn
            color="success"
            @click="submitItem"
            :loading="submittingItem"
            elevation="2"
          >
            <v-icon left>mdi-check</v-icon>
            Add Item
          </v-btn>
          <v-btn text @click="closeAddItemDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restock Price Confirmation Dialog -->
    <v-dialog v-model="restockPriceDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="orange white--text">
          <v-icon left color="white">mdi-cash-sync</v-icon>
          Update Selling Price?
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            The new unit cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) differs from the current unit price (₱{{
              Number(restockItem.unit_price).toLocaleString()
            }}).
          </v-alert>
          <p class="subtitle-2 mb-3">
            Would you like to update the selling price?
          </p>
          <v-radio-group v-model="restockPriceOption" column>
            <v-radio
              value="auto"
              :label="`Accept auto-calculated: ₱${Number(
                restockAutoPrice
              ).toLocaleString()}`"
            ></v-radio>
            <v-radio value="custom" label="Set custom price"></v-radio>
            <v-radio
              value="skip"
              label="Skip — keep current selling price"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="restockPriceOption === 'custom'"
            type="number"
            label="Custom Selling Price"
            v-model="restockCustomPrice"
            outlined
            dense
            prefix="₱"
            class="mt-2"
          ></v-text-field>
          <div
            class="caption grey--text mt-2"
            v-if="restockPriceOption === 'auto'"
          >
            Calculated: Unit Cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) + {{ restockItem.profitable_margin || 0 }}% margin + ₱{{
              Number(restockForm.shipping_cost || 0).toLocaleString()
            }}
            shipping
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockPriceDialog = false">Cancel</v-btn>
          <v-btn
            color="orange"
            dark
            @click="confirmRestockPrice"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Confirm & Restock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restock Dialog (from PO) -->
    <v-dialog v-model="restockDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="green white--text">
          <v-icon left color="white">mdi-plus-box</v-icon>
          Add Stock to Existing Item
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            <strong>{{ restockItem.item_name }}</strong
            ><br />
            Current Stock: <strong>{{ restockItem.quantity }}</strong> | Branch:
            <strong>{{
              restockItem.branch ? restockItem.branch.branch_name : ""
            }}</strong>
          </v-alert>

          <v-text-field
            type="number"
            label="Quantity *"
            v-model="restockForm.quantity"
            outlined
            dense
            prepend-inner-icon="mdi-package-variant-closed-plus"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Unit Cost *"
            v-model="restockForm.unit_cost"
            outlined
            dense
            prepend-inner-icon="mdi-cash"
            prefix="₱"
          ></v-text-field>
          <v-autocomplete
            label="Supplier"
            v-model="restockForm.supplier"
            :items="suppliers"
            item-text="supplier_name"
            item-value="supplier_name"
            outlined
            dense
            prepend-inner-icon="mdi-truck"
          ></v-autocomplete>
          <v-text-field
            label="Purchase Date"
            v-model="restockForm.purchase_date"
            type="date"
            outlined
            dense
            prepend-inner-icon="mdi-calendar"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Shipping Cost"
            v-model="restockForm.shipping_cost"
            outlined
            dense
            prepend-inner-icon="mdi-truck-delivery"
            prefix="₱"
          ></v-text-field>
          <v-radio-group v-model="restockForm.is_vat" row>
            <template v-slot:label>
              <div class="text-body-2 font-weight-bold">Tax Option</div>
            </template>
            <v-radio label="Non-VAT" :value="false"></v-radio>
            <v-radio label="VAT" :value="true"></v-radio>
          </v-radio-group>
          <div class="d-flex align-center">
            <v-text-field
              type="number"
              label="Supplier Warranty"
              v-model="restockForm.supplier_warranty_months"
              outlined
              dense
              prepend-inner-icon="mdi-shield-check"
              class="flex-grow-1 mr-2"
            ></v-text-field>
            <v-select
              v-model="restockForm.supplier_warranty_unit"
              :items="warrantyUnits"
              outlined
              dense
              style="max-width: 140px"
            ></v-select>
          </div>
          <div class="d-flex mb-4">
            <v-chip
              v-for="preset in warrantyPresets[
                restockForm.supplier_warranty_unit || 'months'
              ]"
              :key="preset"
              small
              class="mr-2"
              :color="
                restockForm.supplier_warranty_months == preset
                  ? 'primary'
                  : 'grey lighten-2'
              "
              :dark="restockForm.supplier_warranty_months == preset"
              @click="restockForm.supplier_warranty_months = preset"
            >
              {{ preset
              }}{{
                warrantyUnitShort[
                  restockForm.supplier_warranty_unit || "months"
                ]
              }}
            </v-chip>
          </div>
          <div class="d-flex align-center">
            <v-select
              v-model="restockForm.import_batch_id"
              label="Batch Import"
              :items="import_batches"
              item-value="id"
              item-text="batch_description"
              outlined
              dense
              hide-details
              class="flex-grow-1"
              prepend-inner-icon="mdi-package-variant"
            ></v-select>
            <add-batch-import-modal
              class="ml-2"
              :preset-supplier-id="restockSupplierId"
              @added="onRestockBatchAdded"
            />
          </div>
          <v-text-field
            label="Remarks"
            v-model="restockForm.remarks"
            outlined
            dense
            class="mt-4"
            prepend-inner-icon="mdi-note-text"
          ></v-text-field>
          <v-checkbox
            v-if="!restockItem.is_serialized"
            v-model="restockForm.mark_serialized"
            color="primary"
            dense
            hide-details
            class="mt-0 mb-1"
          >
            <template v-slot:label>
              <span>
                <v-icon small color="primary" class="mr-1">mdi-barcode</v-icon>
                Serialized item — capture a serial per unit
              </span>
            </template>
          </v-checkbox>
          <div
            v-if="!restockItem.is_serialized && restockForm.mark_serialized"
            class="caption orange--text mb-2"
          >
            This marks the item serialized. Existing stock ({{
              restockItem.quantity
            }}) stays un-serialized until you backfill it on the item's Serial
            Numbers tab.
          </div>
          <serial-numbers-input
            v-if="restockItem.is_serialized || restockForm.mark_serialized"
            v-model="restockForm.serials_text"
            :quantity="restockForm.quantity"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockDialog = false">Cancel</v-btn>
          <v-btn
            color="green"
            dark
            @click="submitRestockFromPO"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Add Stock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Combine POs Dialog -->
    <v-dialog v-model="combineDialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="purple white--text">
          <v-icon left color="white">mdi-merge</v-icon>
          <span class="text-h5">Combine Purchase Orders</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeCombineDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" dense outlined class="mb-4">
            <strong>Combining {{ selectedPOs.length }} Purchase Orders</strong>
            <div class="mt-2">
              Supplier: <strong>{{ getCombinedSupplierName() }}</strong>
            </div>
            <div>
              PO Numbers:
              <v-chip
                v-for="po in selectedPOs"
                :key="po.id"
                x-small
                class="mx-1"
              >
                PO-{{ String(po.id).padStart(4, "0") }}
              </v-chip>
            </div>
          </v-alert>

          <h3 class="mb-3">Combined Items</h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Name</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Unit Cost</th>
                  <th class="text-right">Total</th>
                  <th>Source PO</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in combinedItems" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>
                    {{
                      item.canvass_item
                        ? item.canvass_item.item_name
                        : item.item_name || "N/A"
                    }}
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? item.canvass_item.qty
                        : item.qty || "-"
                    }}
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? item.canvass_item.unit
                        : item.unit || "-"
                    }}
                  </td>
                  <td>
                    {{
                      item.canvass_item
                        ? getWinningCost(item.canvass_item)
                        : formatCurrency(item.unit_cost)
                    }}
                  </td>
                  <td class="text-right">
                    {{
                      item.canvass_item
                        ? calculateItemTotal(item.canvass_item)
                        : formatCurrency(
                            (item.qty || 0) * (item.unit_cost || 0)
                          )
                    }}
                  </td>
                  <td>
                    <v-chip x-small color="primary" outlined>
                      PO-{{ String(item.source_po_id).padStart(4, "0") }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="text-right">
                    <strong>Grand Total:</strong>
                  </td>
                  <td class="text-right">
                    <strong>{{ getCombinedGrandTotal() }}</strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCombineDialog">Cancel</v-btn>
          <v-btn
            color="purple"
            @click="confirmCombinePOs"
            :loading="combiningPOs"
          >
            <v-icon left>mdi-merge</v-icon>
            Confirm Combine
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Item Dialog -->
    <v-dialog v-model="editItemDialog" max-width="600px">
      <v-card>
        <v-card-title class="info white--text">
          <v-icon left color="white">mdi-pencil</v-icon>
          <span class="text-h5">Edit Item Details</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeEditItemDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Canvass-based item edit form -->
          <v-form ref="editForm" v-if="editingItem && editingItem.canvass_item">
            <v-text-field
              label="Item Type"
              v-model="editingItem.canvass_item.item_type"
              outlined
              dense
              prepend-inner-icon="mdi-shape"
            ></v-text-field>

            <v-text-field
              label="Item Name"
              v-model="editingItem.canvass_item.item_name"
              outlined
              dense
              prepend-inner-icon="mdi-tag"
            ></v-text-field>

            <v-text-field
              label="Brand/Model"
              v-model="
                editingItem.canvass_item[
                  `supplier_${editingItem.canvass_item.winning_supplier}_item_brand_model`
                ]
              "
              outlined
              dense
              prepend-inner-icon="mdi-label"
            ></v-text-field>

            <v-text-field
              label="Quantity"
              v-model="editingItem.canvass_item.qty"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-counter"
            ></v-text-field>

            <v-text-field
              label="Unit"
              v-model="editingItem.canvass_item.unit"
              outlined
              dense
              prepend-inner-icon="mdi-package"
            ></v-text-field>

            <v-text-field
              label="Unit Cost"
              v-model="
                editingItem.canvass_item[
                  `supplier_${editingItem.canvass_item.winning_supplier}_cost`
                ]
              "
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-cash"
              prefix="₱"
            ></v-text-field>

            <v-textarea
              label="Item Description"
              v-model="editingItem.canvass_item.item_description"
              outlined
              dense
              rows="3"
              prepend-inner-icon="mdi-text"
            ></v-textarea>
          </v-form>

          <!-- Direct purchase item edit form -->
          <v-form ref="editForm" v-else-if="editingItem">
            <v-text-field
              label="Item Name"
              v-model="editingItem.item_name"
              outlined
              dense
              prepend-inner-icon="mdi-tag"
            ></v-text-field>

            <v-text-field
              label="Quantity"
              v-model="editingItem.qty"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-counter"
            ></v-text-field>

            <v-text-field
              label="Unit"
              v-model="editingItem.unit"
              outlined
              dense
              prepend-inner-icon="mdi-package"
            ></v-text-field>

            <v-text-field
              label="Unit Cost"
              v-model="editingItem.unit_cost"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-cash"
              prefix="₱"
            ></v-text-field>

            <v-textarea
              label="Item Description"
              v-model="editingItem.item_description"
              outlined
              dense
              rows="3"
              prepend-inner-icon="mdi-text"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditItemDialog">Cancel</v-btn>
          <v-btn
            color="info"
            @click="saveEditedItem"
            :loading="savingEditedItem"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add New Item to PO Dialog -->
    <v-dialog v-model="addNewItemDialog" max-width="600px">
      <v-card>
        <v-card-title class="success white--text">
          <v-icon left color="white">mdi-plus</v-icon>
          <span class="text-h5">Add New Item to PO</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeAddNewItemDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="newItemForm">
            <v-text-field
              label="Item Name *"
              v-model="newItemForm.item_name"
              outlined
              dense
              prepend-inner-icon="mdi-tag"
            ></v-text-field>

            <v-text-field
              label="Quantity *"
              v-model="newItemForm.qty"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-counter"
            ></v-text-field>

            <v-text-field
              label="Unit *"
              v-model="newItemForm.unit"
              outlined
              dense
              prepend-inner-icon="mdi-package"
            ></v-text-field>

            <v-text-field
              label="Unit Cost *"
              v-model="newItemForm.unit_cost"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-cash"
              prefix="₱"
            ></v-text-field>

            <v-textarea
              label="Item Description"
              v-model="newItemForm.item_description"
              outlined
              dense
              rows="3"
              prepend-inner-icon="mdi-text"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddNewItemDialog">Cancel</v-btn>
          <v-btn color="success" @click="saveNewItem" :loading="savingNewItem">
            <v-icon left>mdi-plus</v-icon>
            Add Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Settings Dialog -->
    <v-dialog v-model="printDialog" max-width="550px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-printer</v-icon>
          <span class="text-h6">Print Settings</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="printDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" outlined dense class="mb-4">
            <span class="text-caption">
              Review delivery details before printing. Defaults are loaded from
              the branch settings.
            </span>
          </v-alert>

          <v-textarea
            label="Delivery Address"
            v-model="printForm.delivery_address"
            outlined
            dense
            rows="3"
            prepend-inner-icon="mdi-map-marker"
          ></v-textarea>

          <v-text-field
            label="Contact Person"
            v-model="printForm.contact_person"
            outlined
            dense
            prepend-inner-icon="mdi-account"
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="printDialog = false">Cancel</v-btn>
          <v-btn
            color="teal"
            @click="confirmCopyAsImage"
            :loading="copyingImage"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Copy as Image
          </v-btn>
          <v-btn color="primary" @click="confirmPrint">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Direct Purchase Dialog -->
    <direct-purchase-dialog
      ref="directPurchaseDialog"
      @notification="(msg, type) => $emit('notification', msg, type)"
      @created="refreshTable"
    />

    <p class="caption grey--text mb-0 mt-4">
      SupplierPurchaseOrderTable.vue -
      src/components/SupplierPurchaseOrder/SupplierPurchaseOrderTable.vue
    </p>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AddBatchImportModal from "../BatchImport/addBatchImportModal.vue";
import DirectPurchaseDialog from "./DirectPurchaseDialog.vue";
import SerialNumbersInput from "../Item/serialNumbersInput.vue";
import { parseSerials, serialError } from "@/utils/serialNumbers";
import {
  printPurchaseOrder,
  copyPurchaseOrderAsImage,
} from "@/utils/purchaseOrderPrint";

export default {
  name: "SupplierPurchaseOrderTable",
  components: {
    AddBatchImportModal,
    DirectPurchaseDialog,
    SerialNumbersInput,
  },

  data() {
    return {
      search: "",
      searchField: "PO Number",
      searchFieldOptions: ["Customer Name", "Supplier", "PO Number"],
      filterStatus: "All",
      filterPurchaseType: "All",
      staleOnly: false,
      staleThreshold: 5,
      staleThresholdOptions: [3, 5, 7, 10, 14],
      viewDialog: false,
      selectedItem: null,
      currentPurchaseOrderItem: null,
      addItemDialog: false,
      submittingItem: false,
      selectedPOs: [],
      combineDialog: false,
      combiningPOs: false,
      combinedItems: [],
      editItemDialog: false,
      editingItem: null,
      editingItemIndex: null,
      savingEditedItem: false,
      addNewItemDialog: false,
      savingNewItem: false,
      newItemForm: {
        item_name: "",
        qty: "",
        unit: "PCS",
        unit_cost: "",
        item_description: "",
      },
      editVatDialog: false,
      editVatStatus: false,
      updatingVat: false,
      editBatchImportDialog: false,
      editBatchCode: "",
      updatingBatchImport: false,
      batchTagLoading: false,
      editPoDetailsDialog: false,
      updatingPoDetails: false,
      editPoDetailsForm: {
        purchase_type: "",
        customer_purchase_order_id: null,
        remarks: "",
        created_by_id: null,
      },
      printDialog: false,
      printItem_: null,
      copyingImage: false,
      printForm: {
        delivery_address: "",
        contact_person: "",
      },
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Forwarded", value: "forwarded" },
        { text: "Paid", value: "paid" },
        { text: "Shipped", value: "shipped" },
        { text: "Received", value: "received" },
      ],
      itemForm: {
        item_name: "",
        item_type_id: "",
        item_sub_type_id: "",
        unit_price: "",
        selling_price: "",
        profitable_margin: "",
        shipping_cost: "",
        supplier: "",
        unit: "PCS",
        initial_import: "",
        is_vat: 0,
        warranty_count: "",
        warranty_duration: "",
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        branch_id: "",
        chart_of_account_id: 1,
        is_serialized: false,
        serials_text: "",
      },
      headers: [
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: 200,
        },
        { text: "PO#", value: "id", sortable: true },
        { text: "Remarks", value: "remarks", sortable: false },
        { text: "Type", value: "purchase_type", sortable: true },
        { text: "Supplier", value: "supplier.supplier_name", sortable: false },
        { text: "Customer Name", value: "customer_name", sortable: false },
        { text: "Items", value: "items_count", sortable: false },
        { text: "Status", value: "is_completed", sortable: true },
        { text: "Is Vat", value: "is_vat_inclusive", sortable: true },
        { text: "Government PO", value: "canvass", sortable: false },

        { text: "Created By", value: "created_by", sortable: false },
        { text: "Date", value: "created_at", sortable: true },
      ],
      itemHeaders: [
        { text: "Action", value: "action", sortable: false, width: "220px" },
        { text: "Item Name", value: "item_name" },
        { text: "Qty", value: "quantity" },
        { text: "Batch", value: "import_batch.batch_description" },
        { text: "Branch", value: "branch.branch_name" },
        { text: "Selling Price", value: "selling_price" },
      ],
      restockDialog: false,
      restockLoading: false,
      restockItem: null,
      restockForm: {
        quantity: "",
        unit_cost: "",
        supplier: "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: 0,
        is_vat: false,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        remarks: "",
        mark_serialized: false,
        serials_text: "",
      },
      warrantyUnits: [
        { text: "Days", value: "days" },
        { text: "Weeks", value: "weeks" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],
      warrantyPresets: {
        days: [30, 60, 90, 180],
        weeks: [4, 12, 26, 52],
        months: [3, 6, 12, 24],
        years: [1, 2, 3, 5],
      },
      warrantyUnitShort: {
        days: "d",
        weeks: "w",
        months: "mo",
        years: "yr",
      },
      restockPriceDialog: false,
      restockPriceOption: "auto",
      restockCustomPrice: "",
      restockAutoPrice: 0,
    };
  },

  computed: {
    ...mapGetters({
      purchase_orders: "purchase_order/purchase_orders",
      loading: "purchase_order/loading",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
      suppliers: "supplier/suppliers",
      import_batches: "importBatch/import_batches",
      chart_of_accounts: "chart_of_account/chart_of_accounts",
      all_items: "item/all_items",
      branches: "branch/branches",
      item_types: "item_type/item_types",
      item_sub_types: "item_sub_type/item_sub_types",
      customer_purchase_orders:
        "customer_purchase_order/customer_purchase_orders",
      all_users: "auth/users",
    }),

    // Resolve the supplier chosen in the restock form (stored as a name) to its
    // id, so the Add Batch Import dialog can pre-select it and auto-fill the
    // batch tag. (JMB-18)
    restockSupplierId() {
      if (!this.restockForm.supplier || !this.suppliers) return null;
      const match = this.suppliers.find(
        (s) => s.supplier_name === this.restockForm.supplier
      );
      return match ? match.id : null;
    },

    branch_filtered_items() {
      if (!this.user || !this.user.branch_id) return [];
      return this.all_items.filter(
        (item) => Number(item.branch_id) === Number(this.user.branch_id)
      );
    },

    filteredSubTypes() {
      if (!this.itemForm.item_type_id) return [];
      return this.item_sub_types.filter(
        (st) => st.item_type_id == this.itemForm.item_type_id
      );
    },

    // POs scoped to the current user's branch (shared by the table + summary).
    branchScopedPOs() {
      let filtered = this.purchase_orders;
      if (this.user && this.user.branch_id) {
        filtered = filtered.filter((po) => {
          // Check if PO has a branch_id that matches user's branch
          if (po.branch_id) {
            return po.branch_id === this.user.branch_id;
          }
          // If PO doesn't have branch_id, check canvass -> customer_purchase_order -> branch_id
          if (po.canvass && po.canvass.customer_purchase_order) {
            return (
              po.canvass.customer_purchase_order.branch_id ===
              this.user.branch_id
            );
          }
          // If no branch info, exclude it
          return false;
        });
      }
      return filtered;
    },

    // Summary tiles shown at the top of the page.
    poSummary() {
      const pos = this.branchScopedPOs;
      const open = pos.filter((po) => !po.is_completed);
      return {
        total: pos.length,
        pending: open.length,
        completed: pos.filter((po) => po.is_completed).length,
        stale: open.filter((po) => this.isStale(po)).length,
      };
    },

    filteredPurchaseOrders() {
      let filtered = this.branchScopedPOs;

      // Stale-only filter (driven by the "no update" summary tile)
      if (this.staleOnly) {
        filtered = filtered.filter((po) => !po.is_completed && this.isStale(po));
      }

      // Filter by status
      if (this.filterStatus && this.filterStatus !== "All") {
        if (this.filterStatus === "Completed") {
          filtered = filtered.filter((po) => po.is_completed);
        } else if (this.filterStatus === "Pending") {
          filtered = filtered.filter((po) => !po.is_completed);
        }
      }

      // Filter by purchase type
      if (this.filterPurchaseType && this.filterPurchaseType !== "All") {
        const typeValue = this.filterPurchaseType.toLowerCase();
        filtered = filtered.filter(
          (po) => (po.purchase_type || "canvass") === typeValue
        );
      }

      return filtered;
    },

    canCombineSelected() {
      if (this.selectedPOs.length < 2) return false;

      // Check if all selected POs have the same supplier
      const firstSupplier = this.getSupplierIdentifier(this.selectedPOs[0]);
      return this.selectedPOs.every(
        (po) => this.getSupplierIdentifier(po) === firstSupplier
      );
    },
  },

  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    // Most recent activity on a PO: the latest of its status-history
    // timestamps, its updated_at, and (fallback) created_at.
    lastActivityAt(po) {
      const times = [];
      if (po.updated_at) times.push(new Date(po.updated_at).getTime());
      if (po.created_at) times.push(new Date(po.created_at).getTime());
      if (Array.isArray(po.status_history)) {
        po.status_history.forEach((h) => {
          if (h && h.changed_at) times.push(new Date(h.changed_at).getTime());
        });
      }
      const valid = times.filter((t) => !isNaN(t));
      return valid.length ? Math.max(...valid) : null;
    },
    // Whole days since the PO was last touched.
    staleDays(po) {
      const last = this.lastActivityAt(po);
      if (!last) return 0;
      return Math.floor((Date.now() - last) / 86400000);
    },
    // Open PO with no activity for >= the chosen threshold.
    isStale(po) {
      return this.staleDays(po) >= this.staleThreshold;
    },
    // Clicking the "No update" tile toggles the stale-only table filter.
    toggleStaleOnly() {
      this.staleOnly = !this.staleOnly;
      if (this.staleOnly) {
        this.filterStatus = "All";
      }
    },
    // Clicking a status tile drives the existing status filter.
    applyStatusTile(status) {
      this.staleOnly = false;
      this.filterStatus = status;
    },
    ...mapActions({
      get_purchase_orders: "purchase_order/get_purchase_orders",
      mark_completed: "purchase_order/mark_completed",
      update_status_action: "purchase_order/update_status",
      update_purchase_order: "purchase_order/update_purchase_order",
      update_batch_import: "purchase_order/update_batch_import",
      get_next_batch_tag: "importBatch/get_next_batch_tag",
      delete_purchase_order: "purchase_order/delete_purchase_order",
      get_suppliers: "supplier/get_suppliers",
      get_import_batches: "importBatch/get_import_batches",
      get_chart_of_accounts: "chart_of_account/get_chart_of_accounts",
      add_item: "item/add_item",
      restock_item: "item/restock_item",
      update_purchase_order_item: "purchase_order/update_purchase_order_item",
      get_all_items: "item/get_all_items",
      get_branches: "branch/get_branches",
      get_item_types: "item_type/get_item_types",
      get_item_sub_types: "item_sub_type/get_item_sub_types",
      get_customer_purchase_orders:
        "customer_purchase_order/get_customer_purchase_orders",
      get_users: "auth/get_users",
    }),

    // Auto-select a batch created via the green + in the restock dialog. (JMB-18)
    onRestockBatchAdded(batch) {
      if (batch && batch.id) {
        this.restockForm.import_batch_id = batch.id;
      }
    },

    async updateStatus(item, newStatus) {
      this.$set(item, "updatingStatus", true);

      try {
        await this.update_status_action({ id: item.id, status: newStatus });
        this.$emit(
          "notification",
          `Purchase order status updated to ${newStatus}`,
          "success"
        );
      } catch (error) {
        console.error("Error updating purchase order status:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update status",
          "error"
        );
      } finally {
        this.$set(item, "updatingStatus", false);
      }
    },

    getStatusColor(status) {
      const colors = {
        pending: "orange",
        forwarded: "blue",
        paid: "purple",
        shipped: "teal",
        received: "green",
        item_registered: "success",
        item_added: "green",
        item_removed: "red",
        vat_status_changed: "indigo",
        batch_import_updated: "deep-purple",
      };
      return colors[status] || "grey";
    },

    customSearchFilter(_value, search, item) {
      if (!search) return true;

      const searchLower = search.toLowerCase();

      switch (this.searchField) {
        case "Customer Name":
          // Search in customer name
          if (
            item.canvass &&
            item.canvass.customer_purchase_order &&
            item.canvass.customer_purchase_order.customer &&
            item.canvass.customer_purchase_order.customer.customer_name &&
            item.canvass.customer_purchase_order.customer.customer_name
              .toLowerCase()
              .includes(searchLower)
          ) {
            return true;
          }
          break;

        case "Supplier":
          // Search in supplier name
          if (item.supplier && item.supplier.supplier_name) {
            if (
              item.supplier.supplier_name.toLowerCase().includes(searchLower)
            ) {
              return true;
            }
          }
          // Also check remarks for supplier name if supplier object not present
          if (
            item.remarks &&
            item.remarks.toLowerCase().includes(searchLower)
          ) {
            return true;
          }
          break;

        case "PO Number": {
          // Search in PO ID
          const poNumber = `PO-${String(item.id).padStart(4, "0")}`;
          if (poNumber.toLowerCase().includes(searchLower)) {
            return true;
          }
          // Also search customer PO number
          if (
            item.canvass &&
            item.canvass.customer_purchase_order &&
            item.canvass.customer_purchase_order.po_number &&
            item.canvass.customer_purchase_order.po_number
              .toLowerCase()
              .includes(searchLower)
          ) {
            return true;
          }
          break;
        }
      }

      return false;
    },

    async refreshTable() {
      await this.get_purchase_orders();
    },

    viewItem(item) {
      this.selectedItem = item;
      this.viewDialog = true;
    },

    async markCompleted(item) {
      const confirmed = await this.$confirm({
        title: "Complete Order",
        message: "Mark this purchase order as completed?",
        color: "success",
        icon: "mdi-check",
      });

      if (!confirmed) return;

      this.$set(item, "completing", true);

      try {
        await this.mark_completed(item.id);
        this.$emit(
          "notification",
          "Purchase order marked as completed!",
          "success"
        );
      } catch (error) {
        console.error("Error marking purchase order as completed:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to mark as completed",
          "error"
        );
      } finally {
        this.$set(item, "completing", false);
      }
    },

    printItem(item) {
      // Resolve branch from PO's branch_id or from canvass -> customer_purchase_order -> branch_id
      const branchId =
        item.branch_id ||
        (item.canvass &&
          item.canvass.customer_purchase_order &&
          item.canvass.customer_purchase_order.branch_id) ||
        null;
      const branch = branchId
        ? this.branches.find((b) => b.id === branchId)
        : null;

      // Pre-fill with branch defaults
      this.printForm.delivery_address = branch?.delivery_information || "";
      this.printForm.contact_person = "";
      this.printItem_ = { item, branch };
      this.printDialog = true;
    },

    confirmPrint() {
      const { item, branch } = this.printItem_;
      printPurchaseOrder(
        item,
        this.suppliers,
        branch,
        this.printForm.delivery_address,
        this.printForm.contact_person
      );
      this.printDialog = false;
    },

    async confirmCopyAsImage() {
      const { item, branch } = this.printItem_;
      this.copyingImage = true;
      try {
        await copyPurchaseOrderAsImage(
          item,
          this.suppliers,
          branch,
          this.printForm.delivery_address,
          this.printForm.contact_person
        );
        this.$emit(
          "notification",
          "Purchase order copied to clipboard as image!",
          "success"
        );
        this.printDialog = false;
      } catch (error) {
        console.error("Error copying as image:", error);
        this.$emit(
          "notification",
          "Failed to copy as image. Please try again.",
          "error"
        );
      } finally {
        this.copyingImage = false;
      }
    },

    async deleteItem(item) {
      const confirmed = await this.$confirm({
        title: "Delete Purchase Order",
        message: "Are you sure you want to delete this purchase order?",
        color: "red",
        icon: "mdi-delete",
      });

      if (!confirmed) return;

      this.$set(item, "deleting", true);

      try {
        await this.delete_purchase_order(item.id);
        this.$emit(
          "notification",
          "Purchase order deleted successfully!",
          "success"
        );
      } catch (error) {
        console.error("Error deleting purchase order:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to delete purchase order",
          "error"
        );
      } finally {
        this.$set(item, "deleting", false);
      }
    },

    closeViewDialog() {
      this.viewDialog = false;
      this.selectedItem = null;
    },

    openEditVatDialog() {
      this.editVatStatus = this.selectedItem.is_vat_inclusive;
      this.editVatDialog = true;
    },

    closeEditVatDialog() {
      this.editVatDialog = false;
      this.editVatStatus = false;
    },

    // Batch Import code edit (JMB-234). Pre-fills with the linked batch's
    // code, or the supplier's next auto-generated tag when the PO has none.
    async openEditBatchImportDialog() {
      if (!this.selectedItem) return;
      this.editBatchCode = this.selectedItem.import_batch
        ? this.selectedItem.import_batch.batch_description
        : "";
      this.editBatchImportDialog = true;
      if (!this.editBatchCode) {
        await this.regenerateBatchCode();
      }
    },

    closeEditBatchImportDialog() {
      this.editBatchImportDialog = false;
      this.editBatchCode = "";
    },

    // Fetch the supplier's next batch tag (same endpoint as the Add New
    // Batch Import modal) into the code field.
    async regenerateBatchCode() {
      const supplierId =
        this.selectedItem &&
        (this.selectedItem.supplier_id ||
          (this.selectedItem.supplier ? this.selectedItem.supplier.id : null));
      if (!supplierId) {
        this.$emit(
          "notification",
          "This purchase order has no supplier — cannot generate a batch tag",
          "error"
        );
        return;
      }
      this.batchTagLoading = true;
      try {
        const data = await this.get_next_batch_tag(supplierId);
        if (data && data.batch_tag) {
          this.editBatchCode = data.batch_tag;
        } else {
          this.$emit(
            "notification",
            (data && data.message) ||
              "This supplier has no batch prefix set — enter a code manually",
            "warning"
          );
        }
      } catch (error) {
        console.error("Failed to fetch next batch tag:", error);
        this.$emit("notification", "Failed to fetch next batch tag", "error");
      } finally {
        this.batchTagLoading = false;
      }
    },

    async confirmEditBatchImport() {
      if (!this.selectedItem || !this.editBatchCode) return;
      this.updatingBatchImport = true;
      try {
        const updatedPO = await this.update_batch_import({
          id: this.selectedItem.id,
          batch_description: this.editBatchCode.trim(),
        });
        this.selectedItem = updatedPO;
        this.$emit(
          "notification",
          "Batch import code updated successfully!",
          "success"
        );
        this.closeEditBatchImportDialog();
      } catch (error) {
        console.error("Error updating batch import code:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update batch import code",
          "error"
        );
      } finally {
        this.updatingBatchImport = false;
      }
    },

    async openEditPoDetailsDialog() {
      if (!this.selectedItem) return;

      // Load data first before setting form values
      const promises = [];
      if (this.customer_purchase_orders.length === 0) {
        promises.push(this.get_customer_purchase_orders());
      }
      if (this.all_users.length === 0) {
        promises.push(this.get_users());
      }
      if (promises.length > 0) {
        await Promise.all(promises);
      }

      this.editPoDetailsForm.purchase_type =
        this.selectedItem.purchase_type || "canvass";
      this.editPoDetailsForm.customer_purchase_order_id =
        this.selectedItem.customer_purchase_order_id ||
        (this.selectedItem.customer_purchase_order
          ? this.selectedItem.customer_purchase_order.id
          : null);
      this.editPoDetailsForm.remarks = this.selectedItem.remarks || "";
      // created_by from Laravel is the relation object {id, name, ...}
      // We need just the integer id for the autocomplete
      const cb = this.selectedItem.created_by;
      let cbId = null;
      if (cb !== null && cb !== undefined) {
        if (typeof cb === "object" && cb.id !== undefined) {
          cbId = parseInt(cb.id);
        } else if (typeof cb === "number" || typeof cb === "string") {
          cbId = parseInt(cb);
        }
      }
      console.log(
        "created_by raw:",
        cb,
        "resolved id:",
        cbId,
        "all_users count:",
        this.all_users.length
      );
      this.editPoDetailsForm.created_by_id = cbId;
      this.editPoDetailsDialog = true;
    },

    closeEditPoDetailsDialog() {
      this.editPoDetailsDialog = false;
    },

    async confirmEditPoDetails() {
      if (!this.selectedItem) return;

      this.updatingPoDetails = true;
      try {
        const updatedPO = {
          id: this.selectedItem.id,
          supplier_id:
            this.selectedItem.supplier_id ||
            (this.selectedItem.supplier ? this.selectedItem.supplier.id : null),
          branch_id: this.selectedItem.branch_id,
          canvass_id:
            this.selectedItem.canvass_id ||
            (this.selectedItem.canvass ? this.selectedItem.canvass.id : null),
          is_completed: this.selectedItem.is_completed,
          is_vat_inclusive: this.selectedItem.is_vat_inclusive,
          status: this.selectedItem.status,
          purchase_type: this.editPoDetailsForm.purchase_type,
          customer_purchase_order_id:
            this.editPoDetailsForm.customer_purchase_order_id,
          remarks: this.editPoDetailsForm.remarks,
          created_by_id: this.editPoDetailsForm.created_by_id,
        };

        await this.update_purchase_order(updatedPO);

        this.selectedItem.purchase_type = this.editPoDetailsForm.purchase_type;
        this.selectedItem.customer_purchase_order_id =
          this.editPoDetailsForm.customer_purchase_order_id;
        this.selectedItem.remarks = this.editPoDetailsForm.remarks;
        this.selectedItem.created_by_id = this.editPoDetailsForm.created_by_id;

        // Update the created_by object for display
        if (this.editPoDetailsForm.created_by_id) {
          const foundUser = this.all_users.find(
            (u) => u.id === this.editPoDetailsForm.created_by_id
          );
          if (foundUser) {
            this.$set(this.selectedItem, "created_by", foundUser);
          }
        } else {
          this.$set(this.selectedItem, "created_by", null);
        }

        // Update customer_purchase_order object for display
        if (this.editPoDetailsForm.customer_purchase_order_id) {
          const foundCpo = this.customer_purchase_orders.find(
            (c) => c.id === this.editPoDetailsForm.customer_purchase_order_id
          );
          if (foundCpo) {
            this.$set(this.selectedItem, "customer_purchase_order", foundCpo);
          }
        } else {
          this.$set(this.selectedItem, "customer_purchase_order", null);
        }

        this.$emit(
          "notification",
          "PO details updated successfully!",
          "success"
        );
        this.closeEditPoDetailsDialog();
        await this.refreshTable();
      } catch (error) {
        console.error("Error updating PO details:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update PO details",
          "error"
        );
      } finally {
        this.updatingPoDetails = false;
      }
    },

    filterCustomerPO(item, queryText) {
      const text = (item.po_number || "").toLowerCase();
      const customerName = (
        (item.customer && item.customer.customer_name) ||
        ""
      ).toLowerCase();
      const search = (queryText || "").toLowerCase();
      return text.indexOf(search) > -1 || customerName.indexOf(search) > -1;
    },

    async confirmEditVat() {
      if (!this.selectedItem) return;

      this.updatingVat = true;
      try {
        const updatedPO = {
          id: this.selectedItem.id,
          supplier_id:
            this.selectedItem.supplier_id ||
            (this.selectedItem.supplier ? this.selectedItem.supplier.id : null),
          branch_id: this.selectedItem.branch_id,
          canvass_id:
            this.selectedItem.canvass_id ||
            (this.selectedItem.canvass ? this.selectedItem.canvass.id : null),
          customer_purchase_order_id:
            this.selectedItem.customer_purchase_order_id ||
            (this.selectedItem.customer_purchase_order
              ? this.selectedItem.customer_purchase_order.id
              : null),
          remarks: this.selectedItem.remarks,
          is_completed: this.selectedItem.is_completed,
          status: this.selectedItem.status,
          is_vat_inclusive: this.editVatStatus,
        };

        await this.update_purchase_order(updatedPO);

        // Update local item
        this.selectedItem.is_vat_inclusive = this.editVatStatus;

        this.$emit(
          "notification",
          "VAT status updated successfully!",
          "success"
        );

        this.closeEditVatDialog();
        await this.refreshTable();
      } catch (error) {
        console.error("Error updating VAT status:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update VAT status",
          "error"
        );
      } finally {
        this.updatingVat = false;
      }
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "N/A";
        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch (e) {
        console.error("Error formatting date:", e, dateTime);
        return "N/A";
      }
    },

    sortedStatusHistory(statusHistory) {
      if (!statusHistory || !Array.isArray(statusHistory)) return [];
      // Sort by date, most recent first
      return [...statusHistory].sort((a, b) => {
        return new Date(b.changed_at) - new Date(a.changed_at);
      });
    },

    capitalizeStatus(status) {
      if (!status) return "";
      if (status === "item_registered") return "Item Registered";
      if (status === "item_added") return "Item Added";
      if (status === "item_removed") return "Item Removed";
      if (status === "vat_status_changed") return "VAT Status Changed";
      if (status === "batch_import_updated") return "Batch Import Updated";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return parseFloat(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    extractSupplierFromRemarks(remarks) {
      if (!remarks) return "Not Set";
      const match = remarks.match(/supplier:\s*(.+?)(?:\s*$|\.)/i);
      return match ? match[1] : "Not Set";
    },

    findSupplierByName(supplierName) {
      if (!supplierName || supplierName === "Not Set" || !this.suppliers)
        return null;

      // Try exact match first
      let supplier = this.suppliers.find(
        (s) =>
          s.supplier_name &&
          s.supplier_name.toLowerCase() === supplierName.toLowerCase()
      );

      // If no exact match, try partial match
      if (!supplier) {
        supplier = this.suppliers.find(
          (s) =>
            s.supplier_name &&
            s.supplier_name.toLowerCase().includes(supplierName.toLowerCase())
        );
      }

      return supplier;
    },

    getWinningCost(canvassItem) {
      if (!canvassItem || !canvassItem.winning_supplier) return "0.00";
      const costField = `supplier_${canvassItem.winning_supplier}_cost`;
      const cost = canvassItem[costField];
      return this.formatCurrency(cost);
    },

    getWinningBrandModel(canvassItem) {
      if (!canvassItem || !canvassItem.winning_supplier) return null;
      const brandModelField = `supplier_${canvassItem.winning_supplier}_item_brand_model`;
      return canvassItem[brandModelField] || null;
    },

    calculateItemTotal(canvassItem) {
      if (!canvassItem || !canvassItem.winning_supplier) return "0.00";
      const costField = `supplier_${canvassItem.winning_supplier}_cost`;
      const cost = canvassItem[costField] || 0;
      const qty = canvassItem.qty || 0;
      return this.formatCurrency(cost * qty);
    },

    calculateGrandTotal(po) {
      if (!po || !po.items) return "0.00";
      let total = 0;
      po.items.forEach((item) => {
        if (item.canvass_item && item.canvass_item.winning_supplier) {
          const costField = `supplier_${item.canvass_item.winning_supplier}_cost`;
          const cost = item.canvass_item[costField] || 0;
          const qty = item.canvass_item.qty || 0;
          total += cost * qty;
        } else if (item.unit_cost) {
          total += (item.unit_cost || 0) * (item.qty || 0);
        }
      });
      return this.formatCurrency(total);
    },

    openAddItemModal(canvassItem, purchaseOrderItem) {
      // Store the purchase order item reference
      this.currentPurchaseOrderItem = purchaseOrderItem;

      // Get the winning supplier's cost
      const winningSupplier = canvassItem.winning_supplier;
      const costField = `supplier_${winningSupplier}_cost`;
      const unitPrice = canvassItem[costField] || 0;

      // Get supplier_id - try multiple sources
      let supplierId = this.selectedItem.supplier_id;

      // If no supplier_id, try to find supplier by name from remarks
      if (!supplierId && this.selectedItem.remarks) {
        const supplierName = this.extractSupplierFromRemarks(
          this.selectedItem.remarks
        );
        const foundSupplier = this.findSupplierByName(supplierName);
        if (foundSupplier) {
          supplierId = foundSupplier.id;
        }
      }

      // If still no supplier, try to get from canvass item winning supplier name
      if (!supplierId && canvassItem.winning_supplier) {
        const supplierNameField = `supplier_${winningSupplier}_name`;
        const winningSupplierName = canvassItem[supplierNameField];
        if (winningSupplierName) {
          const foundSupplier = this.findSupplierByName(winningSupplierName);
          if (foundSupplier) {
            supplierId = foundSupplier.id;
          }
        }
      }

      // Pre-fill the form data
      // Use the winning supplier's brand/model as item name, fallback to item_name if not available
      const itemName =
        this.getWinningBrandModel(canvassItem) || canvassItem.item_name || "";

      // Get supplier name from winning supplier
      let supplierName = "";
      if (canvassItem.winning_supplier) {
        const supplierNameField = `supplier_${winningSupplier}_name`;
        supplierName = canvassItem[supplierNameField] || "";
      }
      if (!supplierName && this.selectedItem.supplier) {
        supplierName = this.selectedItem.supplier.supplier_name || "";
      }

      this.itemForm = {
        item_name: itemName,
        unit_price: unitPrice,
        initial_import: canvassItem.qty || "",
        unit: canvassItem.unit || "PCS",
        supplier: supplierName,
        branch_id: this.user.branch_id,
        chart_of_account_id: 1,
        item_type_id: "",
        item_sub_type_id: "",
        selling_price: "",
        profitable_margin: "",
        shipping_cost: "",
        is_vat: this.selectedItem.is_vat_inclusive ? 1 : 0,
        warranty_count: "",
        warranty_duration: "",
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: this.poImportBatchId(),
        is_serialized: false,
        serials_text: "",
      };

      // Open the dialog
      this.ensurePoBatchInList();
      this.addItemDialog = true;
    },

    openAddItemModalDirect(purchaseOrderItem) {
      this.currentPurchaseOrderItem = purchaseOrderItem;

      let supplierName = "";
      if (this.selectedItem.supplier) {
        supplierName = this.selectedItem.supplier.supplier_name || "";
      }

      this.itemForm = {
        item_name: purchaseOrderItem.item_name || "",
        unit_price: purchaseOrderItem.unit_cost || 0,
        initial_import: purchaseOrderItem.qty || "",
        unit: purchaseOrderItem.unit || "PCS",
        supplier: supplierName,
        branch_id: this.user.branch_id,
        chart_of_account_id: 1,
        item_type_id: "",
        item_sub_type_id: "",
        selling_price: "",
        profitable_margin: "",
        shipping_cost: "",
        is_vat: this.selectedItem.is_vat_inclusive ? 1 : 0,
        warranty_count: "",
        warranty_duration: "",
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: this.poImportBatchId(),
        is_serialized: false,
        serials_text: "",
      };

      this.ensurePoBatchInList();
      this.addItemDialog = true;
    },

    closeAddItemDialog() {
      this.addItemDialog = false;
      // Reset form
      this.itemForm = {
        item_name: "",
        item_type_id: "",
        item_sub_type_id: "",
        unit_price: "",
        selling_price: "",
        profitable_margin: "",
        shipping_cost: "",
        supplier: "",
        unit: "PCS",
        initial_import: "",
        is_vat: 0,
        warranty_count: "",
        warranty_duration: "",
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        branch_id: "",
        chart_of_account_id: 1,
        is_serialized: false,
        serials_text: "",
      };
    },

    calculateSellingPrice() {
      const unitPrice = parseFloat(this.itemForm.unit_price) || 0;
      const profitMargin = parseFloat(this.itemForm.profitable_margin) || 0;
      const shippingCost = parseFloat(this.itemForm.shipping_cost) || 0;

      // Calculate selling price: unit price + (unit price * margin%) + shipping cost
      const profitAmount = unitPrice * (profitMargin / 100);
      const sellingPrice = unitPrice + profitAmount + shippingCost;

      this.itemForm.selling_price = sellingPrice.toFixed(2);
    },

    async submitItem() {
      // Validate required fields
      if (!this.itemForm.item_name) {
        this.$alert({
          title: "Validation",
          message: "Item name is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.itemForm.unit_price) {
        this.$alert({
          title: "Validation",
          message: "Unit price is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.itemForm.initial_import) {
        this.$alert({
          title: "Validation",
          message: "Initial quantity is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.itemForm.selling_price) {
        this.$alert({
          title: "Validation",
          message: "Selling price is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.itemForm.item_type_id) {
        this.$alert({
          title: "Validation",
          message: "Item type is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.itemForm.import_batch_id) {
        this.$alert({
          title: "Validation",
          message: "Import batch is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.itemForm.is_serialized) {
        const err = serialError(
          parseSerials(this.itemForm.serials_text),
          this.itemForm.initial_import
        );
        if (err) {
          this.$alert({
            title: "Serial Numbers",
            message: err,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }

      this.submittingItem = true;

      try {
        const itemPayload = { ...this.itemForm };
        delete itemPayload.serials_text;
        if (this.itemForm.is_serialized) {
          itemPayload.serials = parseSerials(this.itemForm.serials_text);
        }
        const response = await this.add_item(itemPayload);

        // Mark the purchase order item as inserted
        if (this.currentPurchaseOrderItem && this.currentPurchaseOrderItem.id) {
          await this.update_purchase_order_item({
            itemId: this.currentPurchaseOrderItem.id,
            data: { is_inserted_to_items: true },
          });

          // Update the local item to reflect the change
          this.currentPurchaseOrderItem.is_inserted_to_items = true;
        }

        // Add item registration to status history
        if (this.selectedItem && this.selectedItem.id) {
          const itemName =
            this.currentPurchaseOrderItem?.canvass_item?.item_name ||
            this.currentPurchaseOrderItem?.item_name ||
            response.item_name;

          const updatedPO = await this.$store.dispatch(
            "purchase_order/add_status_history",
            {
              po_id: this.selectedItem.id,
              history_entry: {
                status: "item_registered",
                changed_at: new Date().toISOString(),
                changed_by: this.user.name || this.user.username || "Unknown",
                changed_by_id: this.user.id,
                item_id: response.id,
                item_name: itemName,
              },
            }
          );

          // Refresh selectedItem with the updated PO from the store
          if (updatedPO) {
            this.selectedItem = updatedPO;
          }
        }

        this.$emit(
          "notification",
          `Item "${response.item_name}" successfully added to inventory!`,
          "success"
        );
        this.closeAddItemDialog();
      } catch (error) {
        console.error("Error adding item:", error);
        this.$alert({
          title: "Error",
          message: error.response?.data?.message || "Failed to add item",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.submittingItem = false;
      }
    },

    // Combine POs Methods
    getSupplierIdentifier(po) {
      if (po.supplier && po.supplier.id) {
        return `supplier_${po.supplier.id}`;
      }
      if (po.supplier && po.supplier.supplier_name) {
        return `name_${po.supplier.supplier_name.toLowerCase()}`;
      }
      if (po.remarks) {
        const supplierName = this.extractSupplierFromRemarks(po.remarks);
        return `name_${supplierName.toLowerCase()}`;
      }
      return "unknown";
    },

    clearSelection() {
      this.selectedPOs = [];
    },

    openCombineDialog() {
      if (!this.canCombineSelected || this.selectedPOs.length < 2) {
        this.$alert({
          title: "Validation",
          message: "Please select at least 2 POs with the same supplier",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Gather all items from selected POs
      this.combinedItems = [];
      this.selectedPOs.forEach((po) => {
        if (po.items && po.items.length > 0) {
          po.items.forEach((item) => {
            this.combinedItems.push({
              ...item,
              source_po_id: po.id,
            });
          });
        }
      });

      this.combineDialog = true;
    },

    closeCombineDialog() {
      this.combineDialog = false;
      this.combinedItems = [];
    },

    getCombinedSupplierName() {
      if (this.selectedPOs.length === 0) return "";
      const firstPO = this.selectedPOs[0];
      if (firstPO.supplier && firstPO.supplier.supplier_name) {
        return firstPO.supplier.supplier_name;
      }
      if (firstPO.remarks) {
        return this.extractSupplierFromRemarks(firstPO.remarks);
      }
      return "Unknown";
    },

    getCombinedGrandTotal() {
      let total = 0;
      this.combinedItems.forEach((item) => {
        if (item.canvass_item && item.canvass_item.winning_supplier) {
          const costField = `supplier_${item.canvass_item.winning_supplier}_cost`;
          const cost = item.canvass_item[costField] || 0;
          const qty = item.canvass_item.qty || 0;
          total += cost * qty;
        } else if (item.unit_cost) {
          total += (item.unit_cost || 0) * (item.qty || 0);
        }
      });
      return this.formatCurrency(total);
    },

    async confirmCombinePOs() {
      const confirmed = await this.$confirm({
        title: "Combine Purchase Orders",
        message: `Are you sure you want to combine ${this.selectedPOs.length} purchase orders? The original POs will be deleted.`,
        color: "orange",
        icon: "mdi-alert",
      });

      if (!confirmed) return;

      this.combiningPOs = true;

      try {
        // You'll need to add this action to your Vuex store
        const poIds = this.selectedPOs.map((po) => po.id);

        // Call API to combine POs
        await this.$store.dispatch("purchase_order/combine_purchase_orders", {
          po_ids: poIds,
          combined_items: this.combinedItems,
        });

        this.$emit(
          "notification",
          `Successfully combined ${poIds.length} purchase orders!`,
          "success"
        );

        this.closeCombineDialog();
        this.clearSelection();
        await this.refreshTable();
      } catch (error) {
        console.error("Error combining purchase orders:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to combine purchase orders",
          "error"
        );
      } finally {
        this.combiningPOs = false;
      }
    },

    // Edit Item Methods
    openEditItemDialog(item, index) {
      // Create a deep copy to avoid directly mutating the original
      this.editingItem = JSON.parse(JSON.stringify(item));
      this.editingItemIndex = index;
      this.editItemDialog = true;
    },

    closeEditItemDialog() {
      this.editItemDialog = false;
      this.editingItem = null;
      this.editingItemIndex = null;
    },

    async saveEditedItem() {
      if (!this.editingItem) return;

      this.savingEditedItem = true;

      try {
        // Call API to update the item
        const itemData = this.editingItem.canvass_item
          ? this.editingItem.canvass_item
          : {
              item_name: this.editingItem.item_name,
              qty: this.editingItem.qty,
              unit: this.editingItem.unit,
              unit_cost: this.editingItem.unit_cost,
              item_description: this.editingItem.item_description,
            };
        await this.$store.dispatch("purchase_order/update_po_item", {
          po_id: this.selectedItem.id,
          item_id: this.editingItem.id,
          item_data: itemData,
        });

        // Update local data
        this.$set(
          this.selectedItem.items,
          this.editingItemIndex,
          this.editingItem
        );

        // Mark PO as modified
        this.$set(this.selectedItem, "is_modified", true);

        this.$emit("notification", "Item updated successfully!", "success");
        this.closeEditItemDialog();
        await this.refreshTable();
      } catch (error) {
        console.error("Error updating item:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to update item",
          "error"
        );
      } finally {
        this.savingEditedItem = false;
      }
    },

    async removeItemFromPO(item, index) {
      const confirmed = await this.$confirm({
        title: "Remove Item",
        message:
          "Are you sure you want to remove this item from the purchase order?",
        color: "red",
        icon: "mdi-delete",
      });

      if (!confirmed) return;

      try {
        // Call API to remove item
        await this.$store.dispatch("purchase_order/remove_po_item", {
          po_id: this.selectedItem.id,
          item_id: item.id,
        });

        // Remove from local array
        this.selectedItem.items.splice(index, 1);

        // Mark PO as modified
        this.$set(this.selectedItem, "is_modified", true);

        this.$emit("notification", "Item removed successfully!", "success");
        await this.refreshTable();
      } catch (error) {
        console.error("Error removing item:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to remove item",
          "error"
        );
      }
    },

    // Add New Item Methods
    openAddNewItemDialog() {
      this.newItemForm = {
        item_name: "",
        qty: "",
        unit: "PCS",
        unit_cost: "",
        item_description: "",
      };
      this.addNewItemDialog = true;
    },

    closeAddNewItemDialog() {
      this.addNewItemDialog = false;
      this.newItemForm = {
        item_name: "",
        qty: "",
        unit: "PCS",
        unit_cost: "",
        item_description: "",
      };
    },

    async saveNewItem() {
      // Validate
      if (
        !this.newItemForm.item_name ||
        !this.newItemForm.qty ||
        !this.newItemForm.unit ||
        !this.newItemForm.unit_cost
      ) {
        this.$alert({
          title: "Validation",
          message: "Please fill in all required fields",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      this.savingNewItem = true;

      try {
        // Call API to add new item to PO
        const newItem = await this.$store.dispatch(
          "purchase_order/add_item_to_po",
          {
            po_id: this.selectedItem.id,
            item_data: this.newItemForm,
          }
        );

        // Add to local array
        this.selectedItem.items.push(newItem);

        // Mark PO as modified
        this.$set(this.selectedItem, "is_modified", true);

        this.$emit("notification", "New item added successfully!", "success");
        this.closeAddNewItemDialog();
        await this.refreshTable();
      } catch (error) {
        console.error("Error adding new item:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to add new item",
          "error"
        );
      } finally {
        this.savingNewItem = false;
      }
    },

    // The Import Batch auto-generated for / linked to the open PO (JMB-235)
    poImportBatchId() {
      if (!this.selectedItem) return "";
      return (
        this.selectedItem.import_batch_id ||
        (this.selectedItem.import_batch
          ? this.selectedItem.import_batch.id
          : "") ||
        ""
      );
    },

    // The PO's batch may have been created after the dropdown list was
    // fetched (e.g. the PO was created this session) — refresh if missing.
    ensurePoBatchInList() {
      const id = this.poImportBatchId();
      if (id && !this.import_batches.some((b) => b.id == id)) {
        this.get_import_batches();
      }
    },

    openRestockFromPO(item) {
      this.restockItem = item;
      this.ensurePoBatchInList();
      this.restockForm = {
        quantity: this.itemForm.initial_import || "",
        unit_cost: this.itemForm.unit_price || item.unit_price || "",
        supplier: this.itemForm.supplier || item.supplier || "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: this.itemForm.shipping_cost || 0,
        is_vat: item.is_vat ? true : false,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: this.poImportBatchId() || item.import_batch_id || "",
        remarks: "",
        mark_serialized: false,
        serials_text: "",
      };
      this.restockDialog = true;
    },
    async submitRestockFromPO() {
      if (!this.restockForm.quantity || this.restockForm.quantity <= 0) {
        this.$alert({
          title: "Validation",
          message: "Please enter a quantity greater than 0",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.restockForm.unit_cost) {
        this.$alert({
          title: "Validation",
          message: "Please enter a unit cost",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.restockItem.is_serialized || this.restockForm.mark_serialized) {
        const err = serialError(
          parseSerials(this.restockForm.serials_text),
          this.restockForm.quantity
        );
        if (err) {
          this.$alert({
            title: "Serial Numbers",
            message: err,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }
      // Notify whenever the new unit cost differs from the item's current
      // unit cost so the user can decide whether to adjust the selling price.
      // (JMB-19: previously gated behind margin > 0, so items without a
      // configured profitable margin never surfaced this prompt.)
      const newCost = parseFloat(this.restockForm.unit_cost);
      const oldCost = parseFloat(this.restockItem.unit_price);
      const margin = parseFloat(this.restockItem.profitable_margin) || 0;

      if (newCost !== oldCost) {
        const shipping = parseFloat(this.restockForm.shipping_cost) || 0;
        this.restockAutoPrice = Math.round(
          newCost + (newCost * margin) / 100 + shipping
        );
        // Without a margin the auto price is just cost + shipping, so default
        // to a custom selling price and let the user set it explicitly.
        this.restockPriceOption = margin > 0 ? "auto" : "custom";
        this.restockCustomPrice = "";
        this.restockDialog = false;
        this.restockPriceDialog = true;
      } else {
        await this.doRestockFromPO(false);
      }
    },
    async confirmRestockPrice() {
      if (this.restockPriceOption === "skip") {
        await this.doRestockFromPO(false);
      } else if (this.restockPriceOption === "auto") {
        await this.doRestockFromPO(
          true,
          this.restockForm.unit_cost,
          this.restockAutoPrice
        );
      } else if (this.restockPriceOption === "custom") {
        if (!this.restockCustomPrice || this.restockCustomPrice <= 0) {
          this.$alert({
            title: "Validation",
            message: "Please enter a valid custom selling price",
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }
        await this.doRestockFromPO(
          true,
          this.restockForm.unit_cost,
          this.restockCustomPrice
        );
      }
    },
    async doRestockFromPO(updatePrice, newUnitPrice, newSellingPrice) {
      this.restockLoading = true;
      try {
        const payload = { ...this.restockForm };
        delete payload.serials_text;
        if (
          this.restockItem.is_serialized ||
          this.restockForm.mark_serialized
        ) {
          payload.serials = parseSerials(this.restockForm.serials_text);
        }
        if (updatePrice) {
          payload.update_selling_price = true;
          payload.new_unit_price = newUnitPrice;
          payload.new_selling_price = newSellingPrice;
        }
        await this.restock_item({
          itemId: this.restockItem.id,
          payload: payload,
        });

        // Mark the PO item as inserted
        if (this.currentPurchaseOrderItem && this.currentPurchaseOrderItem.id) {
          await this.update_purchase_order_item({
            itemId: this.currentPurchaseOrderItem.id,
            data: { is_inserted_to_items: true },
          });
          this.currentPurchaseOrderItem.is_inserted_to_items = true;
        }

        this.restockLoading = false;
        this.restockDialog = false;
        this.restockPriceDialog = false;
        this.addItemDialog = false;
        this.$alert({
          title: "Success",
          message:
            "Stock added successfully to " +
            this.restockItem.item_name +
            "!" +
            (updatePrice ? " Selling price updated." : ""),
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.restockLoading = false;
        this.$alert({
          title: "Error",
          message: err.response ? err.response.data.error : err.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },

    setItem(item) {
      // Populate form with similar item data
      this.itemForm.item_name = item.item_name;
      this.itemForm.item_type_id = item.item_type_id || "";
      this.itemForm.item_sub_type_id = item.item_sub_type_id || "";
      this.itemForm.shipping_cost = item.shipping_cost;
      this.itemForm.selling_price = item.selling_price;
      this.itemForm.profitable_margin = item.profitable_margin;
      this.itemForm.warranty_count = item.warranty?.warranty_count || "";
      this.itemForm.warranty_duration = item.warranty?.warranty_duration || "";
      this.itemForm.chart_of_account_id = item.chart_of_account_id || 1;
      this.itemForm.supplier = item.supplier || "";
    },
  },

  watch: {
    "itemForm.item_type_id"(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.itemForm.item_sub_type_id = "";
      }
    },
  },

  async created() {
    await Promise.all([
      this.get_purchase_orders(),
      this.get_suppliers(),
      this.get_import_batches(),
      this.get_chart_of_accounts(),
      this.get_all_items({ active_only: 1 }),
      this.get_branches(),
      this.get_item_types(),
      this.get_item_sub_types(),
    ]);
    console.log("supplier purchase order", this.purchase_orders);
  },
};
</script>

<style scoped>
.info-row {
  margin-bottom: 8px;
}
.po-summary-tile {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.po-summary-tile:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}
.po-summary-active {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}
.po-summary-alert {
  border-color: #e53935 !important;
}
.po-stale-days >>> .v-input__slot {
  margin-bottom: 0;
}
.po-stale-days >>> input {
  text-align: center;
}
</style>
